import { useState, useCallback, useEffect } from 'react';
import { getCurrentUser, signOut } from '@aws-amplify/auth';
import { AuthUser } from '@aws-amplify/ui';
import { Hub } from '@aws-amplify/core';
import { logger } from '../utils/logger';

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | null;
}

export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null
  });

  const checkAuthState = useCallback(async () => {
    try {
      const user = await getCurrentUser();
      setAuthState({
        user: user as unknown as AuthUser,
        isAuthenticated: true,
        isLoading: false,
        error: null
      });
    } catch (error) {
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: error instanceof Error ? error : new Error('Authentication failed')
      });
    }
  }, []);

  useEffect(() => {
    checkAuthState();

    const listener = Hub.listen('auth', ({ payload: { event, data } }) => {
      switch (event) {
        case 'signedIn':
          checkAuthState();
          break;
        case 'signedOut':
          setAuthState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null
          });
          break;
      }
    });

    return () => listener();
  }, [checkAuthState]);

  const handleSignOut = async () => {
    try {
      await signOut();
      logger.info('User signed out successfully');
    } catch (error) {
      logger.error('Sign out error:', error);
      throw error;
    }
  };

  return {
    ...authState,
    signOut: handleSignOut
  };
}
